import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "all", label: "All Products" },
  { value: "livestock", label: "Livestock" },
  { value: "fish", label: "Fish Products" },
  { value: "vegetable", label: "Vegetables" },
  { value: "crops", label: "Crops" },
  { value: "palm", label: "Palm Products" }
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="products-page">
      {/* Hero Section */}
      <section className="section-padding bg-stone-900 text-white" data-testid="products-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold mb-6 block">
              Our Products
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Fresh Farm Products
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed">
              Quality livestock and produce directly from AfriGrow Farm. All products are raised 
              and grown with care using sustainable practices.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding bg-stone-50" data-testid="products-grid-section">
        <div className="container-custom">
          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <p className="text-stone-600">
              Showing <span className="font-semibold text-stone-900">{filteredProducts.length}</span> products
            </p>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-stone-300 rounded-full"
                  data-testid="filter-dropdown"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {categories.find(c => c.value === selectedCategory)?.label}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {categories.map((category) => (
                  <DropdownMenuItem
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={selectedCategory === category.value ? "bg-green-50 text-green-700" : ""}
                    data-testid={`filter-${category.value}`}
                  >
                    {category.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-stone-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-stone-200 rounded w-1/4" />
                    <div className="h-6 bg-stone-200 rounded w-3/4" />
                    <div className="h-4 bg-stone-200 rounded w-full" />
                    <div className="h-8 bg-stone-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-stone-500 text-lg">No products found in this category.</p>
              <Button 
                onClick={() => setSelectedCategory("all")}
                variant="outline"
                className="mt-4 rounded-full"
                data-testid="show-all-btn"
              >
                Show All Products
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 card-hover group"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img 
                      src={product.image_url || "https://images.unsplash.com/photo-1585540083814-ea6ee8af9e4f?w=600"} 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${product.available ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                        {product.available ? 'Available' : 'Out of Stock'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <span className="font-accent text-xs uppercase tracking-widest text-terracotta">
                      {product.category}
                    </span>
                    <h3 className="font-heading text-xl font-semibold text-stone-900 mt-2 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${product.available ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                        {product.available ? 'Available' : 'Out of Stock'}
                      </span>
                      <Button 
                        asChild
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                        data-testid={`quote-btn-${product.id}`}
                      >
                        <Link to="/contact">Get Quote</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Info */}
      <section className="section-padding bg-white" data-testid="pricing-info">
        <div className="container-custom">
          <div className="bg-stone-100 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 mb-4">
                  Bulk Orders & Special Pricing
                </h2>
                <p className="text-stone-600 text-lg mb-6">
                  Looking to buy in bulk? We offer competitive pricing for wholesale orders. 
                  Contact us for a custom quote tailored to your needs.
                </p>
                <ul className="space-y-2 text-stone-700">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-600 rounded-full" />
                    Discounts on large orders
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-600 rounded-full" />
                    Regular supply contracts available
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-600 rounded-full" />
                    Delivery within Lagos
                  </li>
                </ul>
              </div>
              <div className="text-center md:text-right">
                <Button 
                  asChild 
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg"
                  data-testid="bulk-order-btn"
                >
                  <Link to="/contact">
                    Request Bulk Quote <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-green-600" data-testid="products-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Can't Find What You Need?
          </h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto mb-10">
            We may have additional products not listed here. Contact us to inquire about specific items.
          </p>
          <Button 
            asChild 
            size="lg"
            className="bg-white text-green-700 hover:bg-stone-100 rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="contact-products-btn"
          >
            <Link to="/contact">
              Contact Us <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
